import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import {
  buildOfferDashboardPath,
  isOfferDashboardRoute,
  type DashboardThemeMode,
  type EmbeddedRouteKey,
} from "../config/dashboard-tenants";
import { getOfferDashboardDefinition } from "../config/offer-dashboard-pages";
import { injectThemeCSS } from "../config/branding";
import { useTheme } from "./ThemeContext";

type OfferDashboardContextType = {
  isEmbeddedMode: boolean;
  activeDashboard: ReturnType<typeof getOfferDashboardDefinition>;
  dashboardSlug: string | null;
  themeMode: DashboardThemeMode;
  semanticTheme: "light" | "dark";
  allowedThemeModes: DashboardThemeMode[];
  setThemeMode: (mode: DashboardThemeMode) => void;
  toggleThemeMode: () => void;
  buildPath: (routeKey: EmbeddedRouteKey) => string;
};

const DEFAULT_THEME_MODE: DashboardThemeMode = "dark";

const OfferDashboardContext = createContext<OfferDashboardContextType | null>(null);

function getSemanticTheme(mode: DashboardThemeMode): "light" | "dark" {
  return mode === "light" ? "light" : "dark";
}

function resolveDashboardThemeMode(
  dashboard: NonNullable<ReturnType<typeof getOfferDashboardDefinition>> | null
): DashboardThemeMode {
  if (!dashboard) return DEFAULT_THEME_MODE;

  const storageKey = `offer-dashboard-theme:${dashboard.slug}`;
  const stored = typeof window !== "undefined"
    ? window.localStorage.getItem(storageKey)
    : null;

  if (stored && dashboard.themeModes.includes(stored as DashboardThemeMode)) {
    return stored as DashboardThemeMode;
  }

  return dashboard.themeModes.includes(DEFAULT_THEME_MODE)
    ? DEFAULT_THEME_MODE
    : dashboard.themeModes[0] || "light";
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "232,91,91";
  const [, red = "e8", green = "5b", blue = "5b"] = result;
  return `${parseInt(red, 16)},${parseInt(green, 16)},${parseInt(blue, 16)}`;
}

function applyEmbeddedBranding(
  activeDashboard: NonNullable<OfferDashboardContextType["activeDashboard"]>,
  themeMode: DashboardThemeMode
) {
  const root = document.documentElement;
  const body = document.body;
  const { branding } = activeDashboard;
  const { colors } = branding;
  const isDark = themeMode === "dark";

  root.classList.remove("blue", "crimson", "dark-bg", "theme-glassmorphism");
  body.style.backgroundImage = "";
  body.style.backgroundColor = "";

  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);
  root.style.setProperty("--brand-bg-light", colors.background.light);
  root.style.setProperty("--brand-bg-dark", colors.background.dark);
  root.style.setProperty("--brand-card-light", colors.card.light);
  root.style.setProperty("--brand-card-dark", colors.card.dark);
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);
  root.style.setProperty("--sidebar-primary", colors.primary);
  root.style.setProperty("--sidebar-ring", colors.ring);
  root.style.setProperty("--sidebar-primary-foreground", "#ffffff");
  root.style.setProperty("--brand-primary-rgb", hexToRgb(colors.primary));

  colors.chart.forEach((color, index) => {
    root.style.setProperty(`--chart-${index + 1}`, color);
  });

  if (activeDashboard.slug === "urban-unity") {
    if (themeMode === "dark") {
      root.classList.add("dark-bg");
      body.style.backgroundImage = "url('/tenants/urban-unity/dark-bg.jpg')";
      body.style.backgroundSize = "cover";
      body.style.backgroundAttachment = "fixed";
      root.style.setProperty("--background", "transparent");
      root.style.setProperty("--card", colors.card.dark);
    } else if (themeMode === "crimson") {
      root.classList.add("crimson");
      body.style.backgroundImage = "url('/tenants/urban-unity/crimson-bg.jpg')";
      body.style.backgroundSize = "cover";
      body.style.backgroundAttachment = "fixed";
      root.style.setProperty("--background", "transparent");
      root.style.setProperty("--card", "rgba(255, 255, 255, 0.93)");
      root.style.setProperty("--foreground", "#ffffff");
      root.style.setProperty("--card-foreground", "#1a0404");
      root.style.setProperty("--popover", "rgba(255, 255, 255, 0.97)");
      root.style.setProperty("--popover-foreground", "#1a0404");
      root.style.setProperty("--border", "rgba(255, 255, 255, 0.28)");
      root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
    } else {
      root.style.setProperty("--background", colors.background.light);
      root.style.setProperty("--card", colors.card.light);
      root.style.removeProperty("--foreground");
      root.style.removeProperty("--card-foreground");
      root.style.removeProperty("--popover");
      root.style.removeProperty("--popover-foreground");
      root.style.removeProperty("--border");
      root.style.removeProperty("--muted-foreground");
    }
    return;
  }

  if (activeDashboard.slug === "the-fitness-ceo" && themeMode === "blue") {
    root.classList.add("blue");
    body.style.backgroundImage = "url('/tenants/the-fitness-ceo/dark-bg.jpg')";
    body.style.backgroundSize = "cover";
    body.style.backgroundAttachment = "fixed";
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", "rgba(255, 255, 255, 0.92)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#1a1a1a");
    root.style.setProperty("--popover", "rgba(15, 55, 140, 0.95)");
    root.style.setProperty("--popover-foreground", "#ffffff");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.2)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.65)");
    ["#615dff", "#504df9", "#ffffff", "#e8bdff", "#09bef5"].forEach((color, index) => {
      root.style.setProperty(`--chart-${index + 1}`, color);
    });
    return;
  }

  root.style.setProperty("--background", isDark ? colors.background.dark : colors.background.light);
  root.style.setProperty("--card", isDark ? colors.card.dark : colors.card.light);
  root.style.removeProperty("--foreground");
  root.style.removeProperty("--card-foreground");
  root.style.removeProperty("--popover");
  root.style.removeProperty("--popover-foreground");
  root.style.removeProperty("--border");
  root.style.removeProperty("--muted-foreground");
}

export function OfferDashboardProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { activeTheme, theme } = useTheme();
  const isEmbeddedMode = isOfferDashboardRoute(location.pathname);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const dashboardSlug = searchParams.get("dashboard");
  const activeDashboard = useMemo(
    () => (isEmbeddedMode ? getOfferDashboardDefinition(dashboardSlug) : null),
    [dashboardSlug, isEmbeddedMode]
  );
  const [themeMode, setThemeModeState] = useState<DashboardThemeMode>(() => resolveDashboardThemeMode(activeDashboard));

  useEffect(() => {
    setThemeModeState(resolveDashboardThemeMode(activeDashboard));
  }, [activeDashboard]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!activeDashboard || !isEmbeddedMode) {
      root.classList.remove("blue", "crimson", "dark-bg");
      document.body.style.backgroundImage = "";
      document.body.style.backgroundColor = "";
      injectThemeCSS(activeTheme, theme === "dark");
      return;
    }

    if (themeMode === "dark") {
      root.classList.add("dark");
    } else if (themeMode === "light" || themeMode === "blue" || themeMode === "crimson") {
      root.classList.remove("dark");
    }

    applyEmbeddedBranding(activeDashboard, themeMode);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(`offer-dashboard-theme:${activeDashboard.slug}`, themeMode);
    }
  }, [activeDashboard, activeTheme, isEmbeddedMode, theme, themeMode]);

  const setThemeMode = (mode: DashboardThemeMode) => {
    if (!activeDashboard || !activeDashboard.themeModes.includes(mode)) return;
    setThemeModeState(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(`offer-dashboard-theme:${activeDashboard.slug}`, mode);
    }
  };

  const toggleThemeMode = () => {
    if (!activeDashboard || activeDashboard.themeModes.length === 0) return;
    const currentIndex = activeDashboard.themeModes.indexOf(themeMode);
    const nextTheme = activeDashboard.themeModes[(currentIndex + 1) % activeDashboard.themeModes.length] || activeDashboard.themeModes[0] || DEFAULT_THEME_MODE;
    setThemeMode(nextTheme);
  };

  const value = useMemo<OfferDashboardContextType>(() => ({
    isEmbeddedMode,
    activeDashboard,
    dashboardSlug: activeDashboard?.slug || null,
    themeMode,
    semanticTheme: getSemanticTheme(themeMode),
    allowedThemeModes: activeDashboard?.themeModes || ["light", "dark"],
    setThemeMode,
    toggleThemeMode,
    buildPath: (routeKey: EmbeddedRouteKey) => {
      if (!activeDashboard) return "/offers";
      return buildOfferDashboardPath(routeKey, activeDashboard.slug);
    },
  }), [activeDashboard, isEmbeddedMode, themeMode]);

  return (
    <OfferDashboardContext.Provider value={value}>
      {children}
    </OfferDashboardContext.Provider>
  );
}

export function useOfferDashboard() {
  const context = useContext(OfferDashboardContext);
  if (!context) {
    throw new Error("useOfferDashboard must be used within an OfferDashboardProvider");
  }
  return context;
}

export function useOptionalOfferDashboard() {
  return useContext(OfferDashboardContext);
}
